import { Add } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Button from "../../components/Button";
import useStrings from "../../hooks/useStrings";
import DataTable from "../../components/DataTable";
import CustomStatusCell from "../../components/CustomStatusCell";
import CodeNameService from "../../services/common/codeName.service";

import { MainComponent } from "../main";
import { Mode, Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { ICodename, IPropListContainer } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



export interface IRequestGetPagedCodename {
    code?: string;
    name?: string;
    group?: string;
    searchText?: string;
    serviceCode?: string;
    organizationId?: string;

    orderby?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
}

export interface IRecordCodename {
    id?: string;
    group?: string;
    nameGroup?: string;
    shortName?: string;
    code?: string;
    name?: string;
    status?: number;
    organizationId?: string;
    createTime?: string;
    createUser?: string;
    updateTime?: string;
    updateUser?: string;
}

interface IModel {
    requestData?: IRequestGetPagedCodename;
}


export const getAllDataCodename = async (data: {
    group: string[],
    serviceCode: string,
    organizationId?: string,
}) => {
    let newDataMap: Map<string, ICodename[]> = new Map();

    try {
        const result = await CodeNameService.getByGroup({
            group: data?.group?.join(","),
            serviceCode: data?.serviceCode,
            organizationId: data?.organizationId,
        });

        data?.group?.forEach((str) => {
            const data: ICodename[] = [];
            [...result?.[str] || []].forEach((e: any) => {
                data.push({
                    name: e.name,
                    code: e.code,
                    detail: e,
                });
            });
            newDataMap.set(str, data);
        });

        return newDataMap;
    } catch (error) { return newDataMap; }
}

const CodenameListContainer = (props: IPropListContainer<IRequestGetPagedCodename> & {
    hidenActionCreate?: boolean;
    hidenActionUpdate?: boolean;
    hidenActionDelete?: boolean;
}) => {
    const Strings = useStrings();

    const {
        userInfo,
        serviceCode,
        onError,
        onSuccess,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [dataRows, setDataRows] = useState<any[]>([]);
    const [model, setModel] = useState<IModel>({} as IModel);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);

    useEffect(() => {
        const onStarted = async () => {
            const newReq: IRequestGetPagedCodename = {
                totalCount: props.requestGetPaged.totalCount || 0,
                pageSize: props.requestGetPaged.pageSize || model?.requestData?.pageSize,
                pageNumber: props.requestGetPaged.pageNumber || model?.requestData?.pageNumber,
                searchText: props.requestGetPaged.searchText || model?.requestData?.searchText,
                organizationId: props.requestGetPaged?.organizationId
            };

            await getPaged(newReq);
        };
        onStarted();
    }, [props.requestGetPaged, userInfo?.userProfile?.organizationId]);

    const getPaged = async (dataReq?: IRequestGetPagedCodename) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = dataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(dataReq?.pageNumber || 1, pageSize, dataReq?.totalCount || 0);

            const newRequest: any = {
                pageSize,
                pageNumber,
                group: dataReq?.group,
                name: dataReq?.searchText,
                serviceCode: serviceCode,
                organizationId: dataReq?.organizationId || userInfo?.userProfile?.organizationId,
            };

            const result = await CodeNameService.getPaged(newRequest);

            setDataRows([...result?.items || []]);

            setModel({
                requestData: {
                    ...newRequest,
                    searchText: newRequest?.name,
                    totalCount: result.totalCount || 0,
                },
            });

            let query: string = `?pageNumber=${newRequest?.pageNumber}&pageSize=${newRequest?.pageSize}`;
            if (!Helpers.isNullOrEmpty(newRequest?.searchText)) {
                query += `&searchText=${newRequest?.searchText}`
            }

            props.onGetPaged({ query, totalCount: result.totalCount || 0 });

        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const handleDeleteRow = async (id: string) => {
        Helpers.showConfirmAlert(Strings.CODE_NAME.CONFIRM_DELETE, async () => {
            try {
                // onShowLoading();
                setLoadingDataTable(true);

                const result = await CodeNameService.delete(id);

                if (result.statusCode === 200) {
                    await getPaged(model.requestData);
                    onSuccess(Strings.CODE_NAME.DELETE_SUCCESS);
                }

            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                // onHideLoading();
                setLoadingDataTable(false);
            }
        });
    };

    return (
        <MainComponent>
            <Grid container spacing={2}>
                {
                    !props.hidenActionCreate &&
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
                            <Button onClick={() => {
                                props.onNavigate({ mode: Mode.Create })
                            }}>
                                <Add />&nbsp;
                                {Strings.Common.ADD_NEW}
                            </Button>
                        </Box>
                    </Grid>
                }
                <Grid item xs={12}>
                    <Card>
                        <Box p={2}>
                            <DataTable
                                showFooter={false}
                                loading={loadingDataTable}
                                totalCount={model.requestData?.totalCount}
                                pageNumber={model.requestData?.pageNumber}
                                rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                                onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                                onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}

                                searchText={model.requestData?.searchText}
                                placeholderSearch={Strings.CODE_NAME.SEARCH_TEXT}
                                onSearch={(val) => {
                                    getPaged({ ...model.requestData, pageNumber: 1, searchText: val })
                                }}
                                onSearchChange={(val: string) => {
                                    setModel({ requestData: { ...model.requestData, searchText: val } })
                                }}

                                table={{
                                    rows: dataRows || [],
                                    columns: [
                                        {
                                            Header: Strings.CODE_NAME.CODE, accessor: "code", width: "120px",
                                        },
                                        {
                                            Header: Strings.CODE_NAME.NAME, accessor: "name", width: "140px",
                                        },
                                        {
                                            Header: Strings.CODE_NAME.SHORT_NAME, accessor: "shortName", width: "140px",
                                        },
                                        {
                                            Header: Strings.Common.STATUS, accessor: "status", width: "150px",
                                            Cell: ({ value }: any) => <CustomStatusCell status={value} />
                                        },
                                    ],
                                }}

                                actionList={(row) => [
                                    {
                                        key: "detail",
                                        iconName: "view",
                                        title: Strings.Common.VIEW_DETAIL,
                                        callback: (row) => {
                                            props.onNavigate({ id: row.id, mode: Mode.View })
                                        },
                                    },
                                    (row.status !== Status.Inactive) && !props.hidenActionUpdate && {
                                        key: "edit",
                                        iconName: "edit",
                                        title: Strings.Common.EDIT,
                                        callback: (row) => {
                                            props.onNavigate({ id: row.id, mode: Mode.Update })
                                        },
                                    },
                                    (row.status !== Status.Inactive) && !props.hidenActionDelete && {
                                        key: "delete",
                                        iconName: "delete",
                                        title: Strings.Common.DELETE,
                                        callback: (row) => handleDeleteRow(row.id),
                                    },
                                ]}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </MainComponent>
    );
};

export default CodenameListContainer;
